import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Award, Target, CheckCircle } from 'lucide-react';
import { learningPrograms } from '../mock/data';

const AILearning = () => {
  return (
    <div className="dark-theme pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="display-large text-white mb-6">
              Enabling People,{' '}
              <span className="text-bluemp-400">Empowering Possibility</span>
            </h1>
            <p className="body-large text-gray-300 max-w-3xl mx-auto mb-8">
              We help professionals understand and use AI confidently and responsibly. Our approach focuses on thinking <em>with</em> AI, not just about AI, through practical, guided enablement programs.
            </p>
            <Link to="/contact" className="btn-primary">
              Start Learning Journey
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Learning Focus Areas */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Focus Areas
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Our learning programs are designed to meet you where you are and take you where you need to be.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {learningPrograms.map((program, index) => (
              <div key={index} className="group dark-hover bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <div className="w-16 h-16 bg-bluemp-400/20 rounded-lg flex items-center justify-center mb-6">
                  <BookOpen size={32} className="text-bluemp-400" />
                </div>
                
                <h3 className="heading-2 text-white mb-4 group-hover:text-bluemp-400 transition-colors">
                  {program.title}
                </h3>
                
                <p className="body-medium text-gray-300 mb-6">
                  {program.description}
                </p>

                <div className="space-y-3">
                  <h4 className="heading-3 text-white">Key Outcomes:</h4>
                  {program.outcomes.map((outcome, outcomeIndex) => (
                    <div key={outcomeIndex} className="flex items-center gap-3">
                      <CheckCircle size={16} className="text-bluemp-400" />
                      <span className="body-small text-gray-300">{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="display-medium text-white mb-6">
                Guided Enablement Approach
              </h2>
              <p className="body-large text-gray-300 mb-8">
                We believe in blending education, demonstration, and applied practice to create lasting capability within your organization. Our approach ensures teams gain confidence and leaders make informed decisions.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="heading-3 text-bluemp-400">1</span>
                  </div>
                  <div>
                    <h3 className="heading-3 text-white mb-2">Education</h3>
                    <p className="body-small text-gray-300">Foundation building through structured learning modules covering AI concepts, applications, and ethical considerations.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="heading-3 text-bluemp-400">2</span>
                  </div>
                  <div>
                    <h3 className="heading-3 text-white mb-2">Demonstration</h3>
                    <p className="body-small text-gray-300">Real-world examples and case studies showing AI applications relevant to your industry and use cases.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="heading-3 text-bluemp-400">3</span>
                  </div>
                  <div>
                    <h3 className="heading-3 text-white mb-2">Applied Practice</h3>
                    <p className="body-small text-gray-300">Hands-on workshops and guided implementation exercises using your organization's actual challenges and data.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
              <h3 className="heading-2 text-bluemp-400 mb-6">Learning Outcomes</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="heading-3 text-white mb-3">For Teams</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-3">
                      <Users size={16} className="text-bluemp-400" />
                      <span className="body-small text-gray-300">Confidence in AI tool usage</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Target size={16} className="text-bluemp-400" />
                      <span className="body-small text-gray-300">Practical application skills</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="heading-3 text-white mb-3">For Leaders</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-3">
                      <Award size={16} className="text-bluemp-400" />
                      <span className="body-small text-gray-300">Informed decision-making capability</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Target size={16} className="text-bluemp-400" />
                      <span className="body-small text-gray-300">Strategic AI investment guidance</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="heading-3 text-white mb-3">For Organizations</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-3">
                      <BookOpen size={16} className="text-bluemp-400" />
                      <span className="body-small text-gray-300">Internal AI capability building</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle size={16} className="text-bluemp-400" />
                      <span className="body-small text-gray-300">Sustainable AI adoption</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Learning Matters */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Why AI Learning Matters
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              In a rapidly evolving AI landscape, knowledge and confidence are your competitive advantages.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Reduce Fear",
                description: "Transform AI anxiety into confidence through understanding and hands-on experience."
              },
              {
                title: "Increase Adoption",
                description: "Enable faster AI tool adoption across your organization with proper training."
              },
              {
                title: "Improve Decisions",
                description: "Make better AI investment decisions based on practical knowledge and experience."
              },
              {
                title: "Build Capability",
                description: "Develop internal AI expertise to reduce dependence on external consultants."
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center group dark-hover p-6 rounded-lg bg-gray-900/30 backdrop-blur-sm">
                <h3 className="heading-3 text-white mb-4 group-hover:text-bluemp-400 transition-colors">
                  {benefit.title}
                </h3>
                <p className="body-small text-gray-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Empower Your Team with AI Knowledge
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Don't let AI happen to you – take control with confidence. Our learning programs ensure your team is ready for the AI-powered future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Start Learning Program
              <ArrowRight size={20} />
            </Link>
            <Link to="/solutions" className="btn-secondary">
              Explore AI Solutions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AILearning;